// The ONE control for "point at your own stock", wherever the configurator asks.
//
// Both the kit contents rows ("which item?") and the rework picker ("which kit or
// SKU are we reworking?") mount this. They are the same question about the same
// Company item master, so they read one feed, narrow it by one rule, and render
// one control — the analogue of nexus_erp's MaterialPicker
// (`nexus_erp/packages/frontend/src/components/shared/MaterialPicker.tsx`), which
// wraps that repo's SearchableSelect the same way.
//
// It owns its OWN search state, per mount. Nexus pages its answer, so the typed
// text is part of the query rather than a filter over a list already fetched — and
// one query shared across every mount means the row being typed in re-fetches the
// list every OTHER row's picker renders from. Row 1's picker would then offer only
// what row 2's customer typed, with row 1's own search box visibly empty and no
// cause on screen. Per-mount state, per-mount query key; TanStack Query dedupes
// mounts that happen to be searching the same text.
//
// It hands back the ITEM, not an id: the list the pick came from lives here, so
// resolving the id anywhere else means keeping a second copy of that list in sync
// with this one.
//
// Nothing here holds the list past a mount: each one revalidates against Nexus,
// so an item that reached Nexus's item master is listed the next time a picker
// opens. An item added in ArgoWMS since Nexus's last scheduled pull is the one
// gap left, and the "check the warehouse" action closes it (useWarehouseRefresh).

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { companyItemLabel, companyItemSearchTerms, type CompanyItem } from 'shared';

import { getCompanyItems, getCompanyItemsByIds } from '../api/company-items.js';
import { SearchableSelect, type SearchableOption } from './SearchableSelect.js';
import { useDebouncedValue } from './useDebouncedValue.js';
import { useWarehouseRefresh } from './useWarehouseRefresh.js';

// Long enough that an ordinary typing rhythm produces one request rather than one
// per character, short enough that a customer who pauses to read the list is not
// waiting on it.
const ITEM_SEARCH_DEBOUNCE_MS = 250;

// What the customer needs in order to tell two similarly-named rows apart, under
// the label. The description is the field a partner records the distinguishing
// wording in ("the blue one with the window"), and it is the field Nexus matches
// on beside name and SKU — so showing it is what makes that match legible: the
// customer can see why a row came back for what they typed.
//
// An inactive row is offered like any other and tagged FIRST: ArgoWMS can hold an
// inactive and a live item under the same name, and the tag is the only thing on
// the row that tells them apart. An active row carries no tag.
function itemHint(
  item: CompanyItem,
  kitLabel: (count: number) => string,
  sourceLabel: (item: CompanyItem) => string,
  inactiveLabel: string,
): string | undefined {
  const parts = [
    item.status === 'inactive' ? inactiveLabel : undefined,
    // Whose stock it is next — the two sources are the coarsest thing separating
    // two similar rows, and it is a distinction the row's own name never carries.
    sourceLabel(item),
    item.description,
    item.is_kit ? kitLabel(item.component_count) : undefined,
  ].filter((v): v is string => typeof v === 'string' && v.length > 0);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

export function CompanyItemPicker({
  id,
  value,
  storedLabel,
  emptyOptionLabel,
  noMatchLabel,
  emptyCatalogLabel,
  offers,
  disabled,
  invalid,
  describedBy,
  onPick,
}: {
  id: string;
  // The stored selection key (CompanyItem.item_id), or '' for no pick.
  value: string;
  // What the caller froze at pick time. Read only as a LAST resort, when neither
  // the current page nor the by-key re-resolve below can name the pick — a stored
  // pick has to keep reading as chosen, and the frozen label is the only thing
  // left that can say so.
  storedLabel?: string | undefined;
  emptyOptionLabel: string;
  // What the list says when the search matches nothing. Defaults to the stock
  // wording ("try fewer words"), which assumes the row is there to be found. A
  // caller whose field is REQUIRED and whose catalog can legitimately be empty
  // passes copy that names the way out instead — see the packaging picker, whose
  // Argo slice is fail-closed on an unfinished catalog review.
  noMatchLabel?: string | undefined;
  // What the open list says when the Company has no stock to offer at all. The
  // picker is always shown, empty or not, so an empty catalog has to say so;
  // otherwise it opens onto a lone clear row. Phrased as what we can see right
  // now, never as a fact about Argo's records: a Nexus outage produces the same
  // empty list.
  emptyCatalogLabel?: string | undefined;
  // Which rows this question offers, judged on the ROW (e.g. isPartnerStock).
  // Absent offers every row the read returned. A stored pick it would refuse
  // still reads as chosen — see the re-resolve below.
  offers?: ((item: CompanyItem) => boolean) | undefined;
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;
  describedBy?: string | undefined;
  // Called with the picked item, or null when the pick is cleared. The item, not
  // the id — see the header.
  onPick: (item: CompanyItem | null) => void;
}): JSX.Element {
  const { t } = useTranslation('quote');
  const [query, setQuery] = useState('');
  const searchTerm = useDebouncedValue(query, ITEM_SEARCH_DEBOUNCE_MS);

  const { data, isFetching } = useQuery({
    queryKey: ['company-items', searchTerm],
    queryFn: () => getCompanyItems(searchTerm),
    // A disabled mount is a read-back, not a picker: it renders the stored label
    // and asks nothing, so the search would fetch a list nobody can open.
    enabled: disabled !== true,
    // Zero, overriding the app-wide default, so every mount revalidates. Not on
    // window focus: that would re-read every open picker each time the tab is
    // revisited.
    staleTime: 0,
    refetchOnWindowFocus: false,
    // The previous page stays on screen while the next one loads, so the list does
    // not empty and re-fill under the customer between keystrokes.
    placeholderData: (previous) => previous,
  });
  const items: CompanyItem[] = (data?.items ?? []).filter((i) => offers?.(i) ?? true);
  // Nexus filled the page to its cap, so it holds more than it sent. Nexus orders
  // by description, so what sorts past the cap is not the least relevant stock,
  // just the latest in the alphabet — with Argo's catalog in reach, most of it.
  // The list says it is cut off and that typing reaches the rest, rather than
  // ending silently.
  const pageCapped = data?.truncated === true;

  // Re-resolve a STORED pick the current page does not contain. Nothing the
  // customer might type is guaranteed to surface it again — the item may have been
  // renamed, or simply sort past the page cap — and without this the trigger would
  // read as the placeholder, which says "your pick is gone". Runs only while the
  // pick is genuinely unresolved, so an ordinary session never issues it.
  const unresolved = value !== '' && !items.some((i) => i.item_id === value);
  const { data: byKey } = useQuery({
    queryKey: ['company-items', 'by-id', value],
    queryFn: () => getCompanyItemsByIds([value]),
    enabled: disabled !== true && unresolved,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const resolvedPick = byKey?.find((i) => i.item_id === value);

  // Re-runs THIS mount's search, with the text as typed right now, so the
  // refreshed page lands under the key the list reads once the debounce settles.
  const { refreshing, refresh } = useWarehouseRefresh({
    queryKey: ['company-items', query],
    fetch: () => getCompanyItems(query, { refreshFromWarehouse: true }),
  });

  const kitLabel = (count: number): string =>
    t('orderServices.assembly.contents.skuKitHint', { count });
  const inactiveLabel = t('stockPicker.inactive');

  const sourceLabel = (item: CompanyItem): string =>
    t(item.is_argo_stock ? 'stockPicker.source.argo' : 'stockPicker.source.company');

  const options: SearchableOption[] = items.map((item) => {
    const hint = itemHint(item, kitLabel, sourceLabel, inactiveLabel);
    return {
      value: item.item_id,
      label: companyItemLabel(item),
      ...(hint !== undefined ? { hint } : {}),
      // Name, SKU, description and unit — the same fields Nexus matched
      // server-side, so typing a second word can never hide a row the server just
      // returned (companyItemSearchTerms is that one list).
      keywords: companyItemSearchTerms(item),
    };
  });

  // The stored pick always has a row, whatever the search says. A picker whose
  // value matches no option would fall back to showing the raw key.
  if (unresolved) {
    const label = resolvedPick ? companyItemLabel(resolvedPick) : (storedLabel ?? value);
    const hint = resolvedPick
      ? itemHint(resolvedPick, kitLabel, sourceLabel, inactiveLabel)
      : undefined;
    options.push({
      value,
      label,
      ...(hint !== undefined ? { hint } : {}),
      ...(resolvedPick ? { keywords: companyItemSearchTerms(resolvedPick) } : {}),
    });
  }

  return (
    <SearchableSelect
      id={id}
      value={value}
      options={options}
      emptyOptionLabel={emptyOptionLabel}
      searchPlaceholder={t('orderServices.assembly.contents.skuSearchPlaceholder')}
      noMatchLabel={noMatchLabel ?? t('orderServices.assembly.contents.skuNoMatch')}
      // A cut-off page with nothing offerable on it is not an empty catalog — the
      // rows this question offers may sort past the cap — so only the cut-off
      // line speaks then.
      emptyListLabel={pageCapped ? undefined : (emptyCatalogLabel ?? t('stockPicker.emptyCatalog'))}
      moreLabel={pageCapped ? t('stockPicker.pageCapped') : undefined}
      onChange={(picked) => {
        if (picked === '') {
          onPick(null);
          return;
        }
        // The resolved-by-key row is a legitimate source here: it is the same
        // shape from the same feed, just fetched by key rather than by text.
        onPick(items.find((i) => i.item_id === picked) ?? resolvedPick ?? null);
      }}
      onSearchChange={setQuery}
      // Offered only where there is a Nexus Company whose item master can be
      // refreshed; a caller with none has nothing in the warehouse to look for.
      refresh={
        data?.has_company === true
          ? {
              label: t('stockPicker.refresh'),
              busyLabel: t('stockPicker.refreshing'),
              busy: refreshing,
              onRefresh: refresh,
            }
          : undefined
      }
      // Only while the customer is waiting on text they typed. A background
      // refetch of an unchanged query would otherwise flash "loading" over a list
      // already on screen.
      // Also while the FIRST page is in flight, so the empty-catalog line is never
      // claimed about a request that has not come back yet.
      // And while a warehouse refresh is running, so "nothing matches" is never
      // said about a list that is about to change.
      loading={refreshing || (isFetching && (searchTerm !== '' || data === undefined))}
      {...(disabled !== undefined ? { disabled } : {})}
      {...(invalid !== undefined ? { invalid } : {})}
      {...(describedBy !== undefined ? { describedBy } : {})}
    />
  );
}
